import { useMemo, useState } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { SortableContext, arrayMove, horizontalListSortingStrategy } from "@dnd-kit/sortable";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { useIsMobile } from "@/lib/use-media-query";
import { MobileStageAccordion } from "./mobile/MobileStageAccordion";
import { MobileMoveToStageSheet } from "./mobile/MobileMoveToStageSheet";
import { useCustomizationLocked } from "@/lib/use-customization-locked";
import { usePermissao } from "@/lib/use-permissao";
import { StageColumn } from "./StageColumn";
import { ProductionKanbanCard } from "./ProductionKanbanCard";
import type { ServiceRow } from "./production-types";
import type { PrazoDoLaboratorio } from "@bookdental/schemas";

type BoardMode = "producao" | "modelo";

const modelDeleteConfirm = (label: string) =>
  `Excluir a etapa "${label}"? Os serviços nela voltam pra "Não possui".`;

/** Board "estilo Notion/Monday". Duas dimensões independentes pra organizar o mesmo serviço —
 *  alternadas pelo toggle Produção/Modelo, nunca as duas ao mesmo tempo:
 *  - Produção: colunas são os `ProductionStage` que o próprio laboratório cria (trilha interna,
 *    independente do status real que o dentista vê). Criar/renomear/reordenar/excluir coluna
 *    acontece direto aqui. Arrastar um card chama `setProductionStage`.
 *  - Modelo: colunas são os `ModelStage` do laboratório (fabricação do modelo físico) — mesma
 *    personalização de Estágio, mas sem `completionBehavior`: arrastar um card chama
 *    `setModelStage`, que nunca influencia `status`.
 *  As duas nunca mudam `status` diretamente — só `setProductionStage` (via resolver) pode. */
export function ProductionStageBoardView({
  rows,
  prazo,
  onOpenService,
}: {
  rows: ServiceRow[];
  prazo: PrazoDoLaboratorio;
  onOpenService?: (row: ServiceRow) => void;
}) {
  const utils = trpc.useUtils();
  // Duas travas diferentes de propósito: `locked` é o plano (personalizar as colunas), `podeMover`
  // é a permissão (mexer no trabalho). Quem só tem `production.view` continua vendo o quadro
  // inteiro, mas sem arrastar card nem configurar coluna — o servidor já recusa, aqui o controle
  // simplesmente não aparece.
  const podeMover = usePermissao("production.manage");
  const locked = useCustomizationLocked() || !podeMover;
  const ehCelular = useIsMobile();
  const [movendoRow, setMovendoRow] = useState<ServiceRow | null>(null);
  const stagesQuery = trpc.prosthesis.stages.list.useQuery();
  const stages = useMemo(() => stagesQuery.data ?? [], [stagesQuery.data]);
  const modelStagesQuery = trpc.prosthesis.modelStages.list.useQuery();
  const modelStages = useMemo(() => modelStagesQuery.data ?? [], [modelStagesQuery.data]);
  const [mode, setMode] = useState<BoardMode>("producao");
  const [draggingRow, setDraggingRow] = useState<ServiceRow | null>(null);
  const [addingColumn, setAddingColumn] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [addingModelColumn, setAddingModelColumn] = useState(false);
  const [newModelLabel, setNewModelLabel] = useState("");

  const invalidateStages = () => utils.prosthesis.stages.list.invalidate();
  const invalidateModelStages = () => utils.prosthesis.modelStages.list.invalidate();
  const invalidateRows = () => utils.prosthesis.incoming.list.invalidate();
  // setProductionStage pode mudar status/rollup "por baixo" via resolver — invalida os dois lados
  // + contadores do dashboard, não só a lista de produção do laboratório.
  const invalidateAfterStageMove = () => {
    utils.prosthesis.incoming.list.invalidate();
    utils.prosthesis.incoming.getById.invalidate();
    utils.prosthesis.list.invalidate();
    utils.prosthesis.countByStatus.invalidate();
  };

  const create = trpc.prosthesis.stages.create.useMutation({
    onSuccess: () => {
      setNewLabel("");
      setAddingColumn(false);
      invalidateStages();
    },
    onError: (e) => toast.error(e.message || "Erro ao criar estágio."),
  });
  const update = trpc.prosthesis.stages.update.useMutation({
    onSuccess: invalidateStages,
    onError: (e) => toast.error(e.message || "Erro ao atualizar estágio."),
  });
  const remove = trpc.prosthesis.stages.delete.useMutation({
    onSuccess: () => {
      toast.success("Estágio excluído.");
      invalidateStages();
      invalidateRows();
    },
    onError: (e) => toast.error(e.message || "Erro ao excluir estágio."),
  });
  const reorder = trpc.prosthesis.stages.reorder.useMutation({
    onSuccess: invalidateStages,
    onError: (e) => toast.error(e.message || "Erro ao reordenar."),
  });
  const setProductionStage = trpc.prosthesis.incoming.setProductionStage.useMutation({
    onSuccess: invalidateAfterStageMove,
    onError: (e) => toast.error(e.message || "Erro ao mover serviço."),
  });

  const createModel = trpc.prosthesis.modelStages.create.useMutation({
    onSuccess: () => {
      setNewModelLabel("");
      setAddingModelColumn(false);
      invalidateModelStages();
    },
    onError: (e) => toast.error(e.message || "Erro ao criar etapa."),
  });
  const updateModel = trpc.prosthesis.modelStages.update.useMutation({
    onSuccess: invalidateModelStages,
    onError: (e) => toast.error(e.message || "Erro ao atualizar etapa."),
  });
  const removeModel = trpc.prosthesis.modelStages.delete.useMutation({
    onSuccess: () => {
      toast.success("Etapa excluída.");
      invalidateModelStages();
      invalidateRows();
    },
    onError: (e) => toast.error(e.message || "Erro ao excluir etapa."),
  });
  const reorderModel = trpc.prosthesis.modelStages.reorder.useMutation({
    onSuccess: invalidateModelStages,
    onError: (e) => toast.error(e.message || "Erro ao reordenar."),
  });
  const setModelStage = trpc.prosthesis.incoming.setModelStage.useMutation({
    onSuccess: invalidateRows,
    onError: (e) => toast.error(e.message || "Erro ao mover serviço."),
  });

  // Serviço Concluído sai do board — já terminou, não precisa mais ocupar espaço no kanban (ainda
  // aparece normalmente na Planilha, que é uma visão de auditoria/histórico, não de fluxo de trabalho).
  const activeRows = useMemo(() => rows.filter((r) => r.service?.status !== "CONCLUIDO"), [rows]);

  const rowsByStage = useMemo(() => {
    const map = new Map<string, ServiceRow[]>();
    map.set("unstaged", []);
    for (const s of stagesQuery.data ?? []) map.set(s.id, []);
    for (const row of activeRows) {
      const key = row.service?.productionStageId ?? "unstaged";
      const bucket = map.get(key) ?? map.get("unstaged")!;
      bucket.push(row);
    }
    return map;
  }, [activeRows, stagesQuery.data]);

  const rowsByModelStage = useMemo(() => {
    const map = new Map<string, ServiceRow[]>();
    map.set("unstaged", []);
    for (const s of modelStagesQuery.data ?? []) map.set(s.id, []);
    for (const row of activeRows) {
      const key = row.service?.modelStageId ?? "unstaged";
      const bucket = map.get(key) ?? map.get("unstaged")!;
      bucket.push(row);
    }
    return map;
  }, [activeRows, modelStagesQuery.data]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const handleDragStart = (e: DragStartEvent) => {
    const data = e.active.data.current as { type?: string; row?: ServiceRow };
    setDraggingRow(data?.type === "card" ? (data.row ?? null) : null);
  };

  const handleDragEnd = (e: DragEndEvent) => {
    setDraggingRow(null);
    if (!e.over || !podeMover) return;
    const data = e.active.data.current as { type?: string; row?: ServiceRow };

    if (data?.type === "column") {
      const list = mode === "modelo" ? modelStages : stages;
      const oldId = String(e.active.id).slice(4);
      const newId = String(e.over.id).slice(4);
      const oldIndex = list.findIndex((s) => s.id === oldId);
      const newIndex = list.findIndex((s) => s.id === newId);
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;
      const orderedIds = arrayMove(list, oldIndex, newIndex).map((s) => s.id);
      (mode === "modelo" ? reorderModel : reorder).mutate({ orderedIds });
      return;
    }

    const row = data?.row;
    if (!row?.service) return;

    if (mode === "modelo") {
      const newModelStageId = e.over.id === "unstaged" ? null : (e.over.id as string);
      if ((row.service.modelStageId ?? null) === newModelStageId) return;
      setModelStage.mutate({ serviceId: row.service.id, modelStageId: newModelStageId });
      return;
    }

    const newStageId = e.over.id === "unstaged" ? null : (e.over.id as string);
    if ((row.service.productionStageId ?? null) === newStageId) return;
    setProductionStage.mutate({ serviceId: row.service.id, productionStageId: newStageId });
  };

  const gruposMobile = useMemo(() => {
    const lista = mode === "producao" ? stages : modelStages;
    const mapa = mode === "producao" ? rowsByStage : rowsByModelStage;
    return [
      { id: null as string | null, rotulo: "Sem estágio", cor: null, linhas: mapa.get("unstaged") ?? [] },
      ...lista.map((s: any) => ({
        id: s.id as string,
        rotulo: s.label as string,
        cor: (s.colorHex ?? null) as string | null,
        linhas: mapa.get(s.id) ?? [],
      })),
    ];
  }, [mode, stages, modelStages, rowsByStage, rowsByModelStage]);

  const movendo = setProductionStage.isPending || setModelStage.isPending;

  /** Mesma mutation do arraste — só muda quem escolhe o destino. */
  const moverPara = (row: ServiceRow, destinoId: string | null) => {
    if (!row.service) return;
    if (mode === "producao") {
      if ((row.service.productionStageId ?? null) === destinoId) return;
      setProductionStage.mutate({ serviceId: row.service.id, productionStageId: destinoId });
    } else {
      if ((row.service.modelStageId ?? null) === destinoId) return;
      setModelStage.mutate({ serviceId: row.service.id, modelStageId: destinoId });
    }
    setMovendoRow(null);
  };

  return (
    <div>
      <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-400 md:hidden dark:text-muted-foreground">
        Organizar quadro por
      </p>
      {/* No celular este seletor é secundário — azul claro com texto azul — pra não competir com
          Estágios/Planilha, que é o principal. Largura pelo conteúdo, não a tela toda. */}
      <div className="mb-3 inline-flex h-10 items-center rounded-md border border-zinc-200 bg-white p-0.5 md:h-8 dark:border-border dark:bg-card">
        {(
          [
            { key: "producao", label: "Produção" },
            { key: "modelo", label: "Modelo" },
          ] as const
        ).map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => setMode(key)}
            className={cn(
              // Visual de 36px (é controle secundário), mas com a área de toque esticada pra 44px
              // por `padding` vertical — sem inchar o desenho.
              "inline-flex h-9 items-center justify-center rounded px-3.5 py-[4px] text-xs font-semibold transition-colors [background-clip:padding-box] md:h-7 md:py-0",
              mode === key
                ? "bg-brand-50 text-brand-700 md:bg-brand-600 md:text-white dark:bg-primary/15 dark:text-primary dark:md:bg-primary dark:md:text-primary-foreground"
                : "text-zinc-600 hover:text-zinc-900 dark:text-muted-foreground dark:hover:text-foreground",
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Celular: acordeões, e o DndContext NEM É MONTADO — arrastar pra coluna fora da tela não
          existe como gesto, e manter os dois montados duplicaria sensores e listeners. Desktop e
          tablet seguem com o quadro horizontal e o arraste exatamente como estavam. */}
      {ehCelular ? (
        <>
          <MobileStageAccordion
            grupos={gruposMobile}
            podeMover={podeMover}
            onAbrir={(row) => onOpenService?.(row)}
            onMover={(row) => setMovendoRow(row)}
          />
          <MobileMoveToStageSheet
            aberto={!!movendoRow}
            paciente={movendoRow?.order.patientName}
            estagios={gruposMobile.map((g) => ({ id: g.id, rotulo: g.rotulo, cor: g.cor }))}
            atualId={
              movendoRow?.service
                ? mode === "producao"
                  ? (movendoRow.service.productionStageId ?? null)
                  : (movendoRow.service.modelStageId ?? null)
                : null
            }
            movendo={movendo}
            onFechar={() => setMovendoRow(null)}
            onEscolher={(destino) => movendoRow && moverPara(movendoRow, destino)}
          />
        </>
      ) : (
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setDraggingRow(null)}
      >
        {mode === "producao" ? (
          <div data-kanban className="-mx-4 flex snap-x gap-4 overflow-x-auto overscroll-x-contain px-4 pb-2 md:mx-0 md:px-0">
            <StageColumn
              podeArrastarCards={podeMover}
              stage={null}
              noneLabel="Sem estágio"
              rows={rowsByStage.get("unstaged") ?? []}
              prazo={prazo}
              onOpenService={onOpenService}
            />

            <SortableContext items={stages.map((s) => `col:${s.id}`)} strategy={horizontalListSortingStrategy}>
              {stages.map((s) => (
                <StageColumn
                  podeArrastarCards={podeMover}
                  key={s.id}
                  stage={s}
                  noneLabel="Sem estágio"
                  locked={locked}
                  rows={rowsByStage.get(s.id) ?? []}
                  prazo={prazo}
                  onOpenService={onOpenService}
                  onRename={(label) => update.mutate({ id: s.id, label })}
                  onColor={(hex) => update.mutate({ id: s.id, colorHex: hex })}
                  onCompletionBehavior={(completionBehavior) => update.mutate({ id: s.id, completionBehavior })}
                  onDelete={() => remove.mutate({ id: s.id })}
                />
              ))}
            </SortableContext>

            {!locked && (
            <div className="flex w-[15rem] shrink-0 snap-start flex-col gap-2 pt-8 md:w-64">
              {addingColumn ? (
                <div className="flex gap-1.5 rounded-xl border border-dashed border-zinc-300 p-2 dark:border-border">
                  <input
                    autoFocus
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && newLabel.trim().length >= 2) create.mutate({ label: newLabel.trim() });
                      if (e.key === "Escape") setAddingColumn(false);
                    }}
                    placeholder="Nome do estágio..."
                    className="h-8 flex-1 rounded-md border border-zinc-200 bg-white px-2.5 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground"
                  />
                  <button
                    type="button"
                    disabled={create.isPending || newLabel.trim().length < 2}
                    onClick={() => create.mutate({ label: newLabel.trim() })}
                    className="inline-flex h-8 shrink-0 items-center rounded-md bg-brand-600 px-2.5 text-xs font-semibold text-white disabled:opacity-50"
                  >
                    Criar
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setAddingColumn(true)}
                  className="inline-flex h-11 items-center gap-1.5 rounded-xl border border-dashed border-zinc-300 px-3 text-xs font-semibold text-zinc-500 transition-colors hover:border-brand-300 hover:text-brand-700 md:h-9 dark:border-border dark:text-muted-foreground"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Novo estágio
                </button>
              )}
            </div>
            )}
          </div>
        ) : (
          <div data-kanban className="-mx-4 flex snap-x gap-4 overflow-x-auto overscroll-x-contain px-4 pb-2 md:mx-0 md:px-0">
            <StageColumn
              podeArrastarCards={podeMover}
              stage={null}
              noneLabel="Não possui"
              rows={rowsByModelStage.get("unstaged") ?? []}
              prazo={prazo}
              onOpenService={onOpenService}
            />

            <SortableContext items={modelStages.map((s) => `col:${s.id}`)} strategy={horizontalListSortingStrategy}>
              {modelStages.map((s) => (
                <StageColumn
                  podeArrastarCards={podeMover}
                  key={s.id}
                  stage={s}
                  noneLabel="Não possui"
                  configureAriaLabel="Configurar etapa"
                  deleteConfirmMessage={modelDeleteConfirm}
                  locked={locked}
                  rows={rowsByModelStage.get(s.id) ?? []}
                  prazo={prazo}
                  onOpenService={onOpenService}
                  onRename={(label) => updateModel.mutate({ id: s.id, label })}
                  onColor={(hex) => updateModel.mutate({ id: s.id, colorHex: hex })}
                  onDelete={() => removeModel.mutate({ id: s.id })}
                />
              ))}
            </SortableContext>

            {!locked && (
            <div className="flex w-[15rem] shrink-0 snap-start flex-col gap-2 pt-8 md:w-64">
              {addingModelColumn ? (
                <div className="flex gap-1.5 rounded-xl border border-dashed border-zinc-300 p-2 dark:border-border">
                  <input
                    autoFocus
                    value={newModelLabel}
                    onChange={(e) => setNewModelLabel(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && newModelLabel.trim().length >= 2)
                        createModel.mutate({ label: newModelLabel.trim() });
                      if (e.key === "Escape") setAddingModelColumn(false);
                    }}
                    placeholder="Nome da etapa..."
                    className="h-8 flex-1 rounded-md border border-zinc-200 bg-white px-2.5 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground"
                  />
                  <button
                    type="button"
                    disabled={createModel.isPending || newModelLabel.trim().length < 2}
                    onClick={() => createModel.mutate({ label: newModelLabel.trim() })}
                    className="inline-flex h-8 shrink-0 items-center rounded-md bg-brand-600 px-2.5 text-xs font-semibold text-white disabled:opacity-50"
                  >
                    Criar
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setAddingModelColumn(true)}
                  className="inline-flex h-11 items-center gap-1.5 rounded-xl border border-dashed border-zinc-300 px-3 text-xs font-semibold text-zinc-500 transition-colors hover:border-brand-300 hover:text-brand-700 md:h-9 dark:border-border dark:text-muted-foreground"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Nova etapa
                </button>
              )}
            </div>
            )}
          </div>
        )}

        <DragOverlay dropAnimation={null}>
          {draggingRow && <ProductionKanbanCard row={draggingRow} prazo={prazo} dragging />}
        </DragOverlay>
      </DndContext>
      )}
    </div>
  );
}
